import React, { useState, useEffect, forwardRef } from "react";
import LazyLoad from "react-lazyload";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";
import cx from "classnames";
import useBreakpoint from "utils/hooks/useBreakpoint";
import tw from "tailwind.macro";
import breakpoints from "utils/breakpoints";
import { AspectRatio } from "react-aspect-ratio";
import OpenNewTab from "components/icons/OpenNewTab";

const boxShadow = css`
  box-shadow: 0px 10px 40px rgba(0, 0, 0, 0.3);
`;

const Container = styled.div`
  min-width: 14.5rem;
  min-height: 12.5rem;
  max-width: 100%;
  transition: all 0.4s ease-out;

  ${boxShadow}

  @media ${breakpoints.md} {
    min-width: 27.625rem;
    min-height: 21rem;
  }

  @media ${breakpoints.lg} {
    box-shadow: 0px 0px 40px rgba(0, 0, 0, 0);
    &:hover {
      ${boxShadow}
    }
  }
`;

const Body = styled.div`
  ${Container}:hover & {
    ${tw`bg-mattamy-blue`}
  }
`;

const Cta = styled.div`
  min-height: 3.125rem;
`;

const PromoCard = forwardRef(
  (
    {
      promoCard = {},
      className,
      handleShowModal,
      showNewSchemaColor,
      newSchemaColorHexaCode,
      newSchemaTextColorHexaCode,
      ...rest
    },
    ref
  ) => {
    const {
      imageUrl,
      imageAlt,
      title,
      ctas = [],
      promoNoDetailPage = false,
      promoDisclaimerText,
    } = promoCard;

    const breakpoint = useBreakpoint(
      typeof window !== "undefined" && window.innerWidth
    );
    const isMobile = ["sm", "xs", "md"].includes(breakpoint);
    const cta = ctas[0] || {};
    const [zoomLevel, setZoomLevel] = useState(100);

    useEffect(() => {
      const newZoomLevel = Math.round(window.devicePixelRatio * 100);
      setZoomLevel(newZoomLevel);
      const handleResize = () => {
        const newZoomLevel = Math.round(window.devicePixelRatio * 100);
        setZoomLevel(newZoomLevel);
      };

      window.addEventListener("resize", handleResize);

      return () => {
        window.removeEventListener("resize", handleResize);
      };
    }, []);

    return (
      <div className={cx("flex justify-center", className)} {...rest}>
        <a
          ref={ref}
          href={!promoNoDetailPage ? cta.href : null}
          tabIndex={promoNoDetailPage ? 0 : null}
          onKeyPress={(event) => {
            if (
              event.key === "Enter" ||
              event.which === 13 ||
              event.keyCode === 13
            ) {
              window.dataLayer.push({ event: "view-promotion" });
              typeof handleShowModal === "function" &&
                promoNoDetailPage &&
                handleShowModal(promoDisclaimerText);
            }
          }}
          aria-haspopup="dialog"
          aria-expanded={promoNoDetailPage}
          target={cta.target || "_self"}
          onClick={(e) => {
            window.dataLayer.push({ event: "view-promotion" });
            typeof handleShowModal === "function" &&
              promoNoDetailPage &&
              handleShowModal(promoDisclaimerText);
          }}
          aria-label={
            "View " +
            title +
            " information." +
            (cta && cta.target && cta.target === "_blank"
              ? "(Opens in new tab)"
              : "")
          }
        >
          <Container
            style={
              zoomLevel >= 400 || zoomLevel === 250
                ? {
                    scale: ".75",
                  }
                : {
                    scale: "1",
                  }
            }
            className={cx(
              "relative w-full flex flex-col rounded overflow-hidden transform cursor-pointer bg-white",
              {
                "hover:-translate-y-2": !isMobile,
              }
            )}
          >
            <AspectRatio ratio={"470/278"} className="w-full h-full">
              <div tabIndex={-1} className="flex flex-col">
                <AspectRatio
                  ratio={
                    (isMobile && zoomLevel <= 200) || zoomLevel >= 300
                      ? "433/278"
                      : "433/278"
                  }
                  className="w-full"
                >
                  <LazyLoad>
                    <img
                      tabIndex={-1}
                      className="flex-1 object-cover h-full min-w-full bg-white"
                      src={imageUrl}
                      alt={imageAlt}
                    />
                  </LazyLoad>
                </AspectRatio>
                <Body
                  className={cx("rounded-b px-5 flex flex-col", {
                    "bg-mattamy-blue": !showNewSchemaColor,
                    "bg-black": showNewSchemaColor,
                  })}
                >
                  <Cta
                    id="promoCard"
                    tabIndex="-1"
                    role="button"
                    className="w-full flex items-center justify-center rounded font-trade-gothic-20 uppercase text-white pt-1 text-center"
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        if (cta.href) {
                          window.location.href = cta.href
                        }
                      }
                    }}
                    onBlur={(e) => {
                      e.preventDefault()
                      if(rest && rest.filterCardsLenght && rest.ItemCLoseClickRef){
                        if (rest.cardIndex + 1 == rest.filterCardsLenght.length) {
                          rest.ItemCLoseClickRef.current.focus();
                         
                        }
                      }
                     }}
                  >
                    {cta.text}
                    {cta && cta.target && cta.target === "_blank" && (
                      <OpenNewTab className="mb-1 ml-1 w-5" />
                    )}
                  </Cta>
                </Body>
              </div>
            </AspectRatio>
          </Container>
        </a>
      </div>
    );
  }
);

PromoCard.propTypes = {
  promoCard: PropTypes.object,
};

export default PromoCard;
